import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import defaultLogo from 'assets/images/logos/defaultlogo.png';

export default function AllUsers() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const [isLoading, setIsLoading] = useState(true);
    const [usersData, setUsersData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isArchived, setIsArchived] = useState(false);
    const navigate = useNavigate();

    const fetchUsers = async () => {
        const params = {
            page: currentPage,
            limit: rowsPerPage,
            firstName: searchTerm,
            ...(isArchived !== null ? { isActive: !isArchived } : {})
        };
        try {
            if (!bearerToken) return;
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}auth-service/auth/all`,
                {
                    params,
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response.data?.success) {
                setUsersData(response.data.data);
                setFilteredUsers(response.data.data);
                setTotalItemsCount(response.data.totalItems || 0);
                setTotalPagesCount(response.data.totalPages || 0);
                setTotalPagesCount(response.data.totalPages || 0);
            } else throw new Error(response.data?.message || 'Error in fetching users');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const filterCategory = (term) => {
        if (!term) {
            setFilteredUsers(usersData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = usersData.filter((user) =>
            user.firstName.toLowerCase().includes(lowerCaseTerm) || // Filter by name
            user.lastName?.toLowerCase().includes(lowerCaseTerm) // Optional: Filter by description
        );
        setFilteredUsers(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterCategory(term);
    };

    const handleActionClick = (id, action) => {
        const actions = {
            view: () => navigate(`/venues/single-venue/${id}`),
            edit: () => navigate(`/venues/edit-venue/${id}`),
            delete: () => handleDelete(id),
            archive: () => handleArchive(id, true),
            unarchive: () => handleArchive(id, false),
        };
        actions[action]?.();
    };

    const handleArchive = async (id, isArchived) => {
        try {
            const url = `${apiUrl}auth-service/auth/${isArchived ? 'deactivate-user' : 'active-user'}/${id}`;

            const response = await axios.patch(url, {}, {
                headers: { 'Authorization': `Bearer ${bearerToken}` }
            });

            if (response?.data.success) {
                toast.warning(response.data.message || `User has been ${isArchived ? 'deactivated' : 'activated'}`);
                fetchUsers();
            } else {
                throw new Error(response?.data.message || 'Unable to update');
            }
        } catch (error) {
            const errorMsg = error.response?.data.message || error.message;
            console.error(errorMsg);
            toast.error(errorMsg);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [bearerToken, currentPage, rowsPerPage, searchTerm, isArchived]);

    const columns = [
        {
            key: 'name', label: 'Name',
            render: (row) => (
                <div className="flex items-center gap-3">
                    <img
                        src={row?.logo?.secure_url || defaultLogo}
                        alt={`${row.firstName} icon`}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <span>{row.firstName}</span>
                    <span>{row.lastName}</span>
                </div>
            ),
        },
        { key: 'email', label: 'Email', render: (row) => (row.email) },
        { key: 'phone', label: 'Phone', render: (row) => (row.phone) },
        // {
        //     key: 'status',
        //     label: 'Status',
        //     render: (row) => (
        //         <div
        //             className={`border rounded ${row.isActive ? 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]' : 'bg-red-100 text-red-700 border-red-500'} w-fit h-7 flex items-center`}
        //         >
        //             <div className="py-1 px-3">{row.isActive ? 'Active' : 'Inactive'}</div>
        //         </div>
        //     ),
        // },
        // { key: 'eventCount', label: 'Event Count', render: (row) => (row.eventCount) },
    ];

    const getStatusColor = (status) => {
        const statusColors = {
            active: 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]',
            published: 'bg-[#F86624] bg-opacity-15 text-[#EF4A00] border-[#EF4A00]',
            draft: 'bg-red-100 text-red-700 border-red-500',
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700';
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <DataTable
                    title={'All Users'}
                    rows={filteredUsers} columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: true, showHeaderTwo: false, viewOption: false, editOption: false, addOption: false, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true, archiveFilterOption: true }}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    // eventStatus={eventStatus}
                    // onStatusChange={(status) => setEventStatus(status)}
                    // categories={eventCategories}
                    // onCategoryChange={(id) => setSelectedCategoryId(id)}
                    // category={selectedCategoryName}
                    // onDateChange={(date) => setSelectedDate(date)}
                    // filteredDate={selectedDate}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    loaderStatus={isLoading}
                    isArchivedStatus={isArchived}
                    onToggle={(value) => setIsArchived(value)}
                    addButtonOnlyOption={true}
                    addButtonTitle={"Add User"}
                    handleDialogBoxes={() => navigate("/addUser")}
                />
            </div>
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <FooterTailwind />
        </TailwindLayout>
    )
}